"use client"

import type React from "react"
import { motion } from "framer-motion"
import { useInView } from "framer-motion"
import { useRef } from "react"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import Image from "next/image"
import { Calendar, Users, Award, TrendingUp, Factory, Globe, Lightbulb, Target } from "lucide-react"
import Img_2022 from "@/assets/2022.jpg"
import Img_2024 from "@/assets/2024.jpg"
import Img_2025 from "@/assets/2025.jpg"

interface Milestone {
  year: string
  title: string
  description: string
  story: string
  icon: React.ReactNode
  image?: any
  achievements?: string[]
}

const milestones: Milestone[] = [
  {
    year: "2022",
    title: "Thành Lập CỬU LONG YARN JSC",
    description: "Công ty chính thức được thành lập với khát vọng nâng tầm ngành Sợi PP tại Việt Nam.",
    story:
      "CỬU LONG YARN JSC được sáng lập bởi những người có tâm huyết với ngành nhựa kỹ thuật. Xuất phát từ một xưởng nhỏ, công ty đặt mục tiêu mang đến những sản phẩm sợi PP chất lượng cao, phục vụ thị trường trong nước và từng bước vươn ra quốc tế.",
    icon: <Lightbulb className="h-6 w-6" />,
    image: Img_2022,
    achievements: [],
  },
  {
    year: "2024",
    title: "Tăng Tốc Sản Xuất",
    description: "Đầu tư máy móc hiện đại và mở rộng quy mô sản xuất.",
    story:
      "Chỉ sau hai năm hoạt động, Sợi Cửu Long đã quyết định dời nhà máy vào Cụm công nghiệp Dốc 47, thuộc Phường Tam Phước, Tp. Biên Hòa, tỉnh Đồng Nai, đánh dấu bước chuyển mình lớn về cơ sở hạ tầng và điều kiện sản xuất. Bên cạnh đó, công ty đã đầu tư mới và nâng cấp dây chuyền sản xuất để tăng gấp đôi công suất từ 500 tấn/ năm lên hơn 1,000 tấn/ năm.",
    icon: <Factory className="h-6 w-6" />,
    image: Img_2024,
    achievements: ["Dời vào Cụm công nghiệp", "Nâng cấp cơ sở vật chất, máy móc thiết bị", "Tăng gấp đôi công suất"],
  },
  {
    year: "2025",
    title: "Khẳng Định Uy Tín Thị Trường",
    description: "Sau gần 4 năm hoạt động, sản phẩm sợi PP Multi của Công ty Cổ phần Sợi Cửu Long đã được biết đến rộng rãi với hệ thống khách hàng trên 20 tỉnh thành, cũng như trở thành đối tác chiến lược của các tên tuổi hàng đầu Đông Nam Á trong lĩnh vực sản xuất dây thừng PP, bao bì, có sản phẩm xuất khẩu đến các thị trường lớn như Mỹ, Nhật Bản, Úc và Bắc Âu",
    story:
      "Độ bền chắc thuộc top đầu thị trường Việt Nam, chất lượng ổn định và thời gian giao hàng linh hoạt giúp Sợi Cửu Long chiếm được lòng tin của các khách hàng lớn",
    icon: <Award className="h-6 w-6" />,
    image: Img_2025,
    achievements: ["Độ bền chắc G/D thuộc top cao nhất thị trường Việt Nam: G/D > 7.4", "Sản phẩm làm từ Sợi PP của công ty được xuất khẩu đến 5 thị trường lớn"],
  }
]

const achievements = [
  { number: "15+", label: "Năm kinh nghiệm", icon: <Calendar className="h-8 w-8" /> },
  { number: "100+", label: "Nhân viên", icon: <Users className="h-8 w-8" /> },
  { number: "5000+", label: "Khách hàng", icon: <TrendingUp className="h-8 w-8" /> },
  { number: "50+", label: "Tỉnh thành", icon: <Globe className="h-8 w-8" /> },
]

export default function HistorySection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  return (
    <div className="py-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-secondary via-secondary/95 to-secondary text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23DB7F29' fillOpacity='0.3'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl md:text-6xl font-display text-white mb-6">Về chúng tôi</h1>
            <div className="w-32 h-1 bg-primary mx-auto mb-8"></div>
            <p className="text-xl max-w-4xl mx-auto leading-relaxed text-white/90">
              Tuy mới được thành lập vào tháng 01/2022, nhưng sự kết hợp giữa đội ngũ nhân sự có chuyên môn cao cùng trang thiết bị, máy móc hiện đại được đầu tư mới, đã tạo ra một công ty có năng lực sản xuất sợi PP Multi cường lực với chất lượng hàng đầu
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <Card className="p-8 h-full bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-display text-secondary">TẦM NHÌN</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Trở thành nhà sản xuất sợi PP Multi cường lực hàng đầu Việt Nam, đáp ứng yêu cầu xuất khẩu đến các thị trường lớn trên Thế giới, tiến tới đa dạng các sản phẩm chất lượng cao làm sợi PP Multi
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card className="p-8 h-full bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mr-4">
                    <Lightbulb className="h-6 w-6 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-display text-secondary">SỨ MỆNH</h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Cung cấp Sợi PP Multi chất lượng cao với giá cả cạnh tranh, tham gia mạnh mẽ vào chuỗi cung ứng, góp phần đưa các sản phẩm làm từ sợi PP Multi của  Việt Nam ra thị trường quốc tế
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white" ref={ref}>
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-display text-secondary mb-6">HÀNH TRÌNH PHÁT TRIỂN</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Những cột mốc quan trọng đánh dấu sự trưởng thành và phát triển vượt bậc của CỬU LONG YARN JSC
            </p>
            <div className="w-24 h-1 bg-primary mx-auto mt-6"></div>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-primary/50 to-primary hidden lg:block"></div>

            <div className="space-y-16">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  className={`flex flex-col lg:flex-row gap-8 items-center ${index % 2 === 1 ? "lg:flex-row-reverse" : ""
                    }`}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                >
                  <div className="lg:w-1/2 relative">
                    <Card className="p-8 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50 border-l-4 border-l-primary">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                          <div className="text-primary">{milestone.icon}</div>
                        </div>
                        <Badge variant="outline" className="text-primary border-primary font-industrial px-4 py-2">
                          {milestone.year}
                        </Badge>
                      </div>

                      <h3 className="text-2xl font-heading text-secondary mb-4">{milestone.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4 text-lg">{milestone.description}</p>

                      <div className="bg-primary/5 p-4 rounded-lg mb-6">
                        <p className="text-gray-700 italic leading-relaxed">{milestone.story}</p>
                      </div>

                      {milestone.achievements && milestone.achievements.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-semibold text-secondary mb-3 font-industrial">THÀNH TỰU NỔI BẬT:</h4>
                          {milestone.achievements.map((achievement, idx) => (
                            <div key={idx} className="flex items-center space-x-3">
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                              <span className="text-gray-600 font-medium">{achievement}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>

                    {/* Timeline dot */}
                    <div className="absolute top-8 -right-6 w-4 h-4 bg-primary rounded-full border-4 border-white shadow-lg hidden lg:block"></div>
                  </div>

                  <div className="lg:w-1/2">
                    <motion.div className="relative" whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                      <Image
                        src={milestone.image}
                        alt={milestone.title}
                        className="rounded-xl max-h-[400px] object-cover"
                      />
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}
